import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Video, Play, Users, Clock, Calendar, Plus, RefreshCw,
  MonitorPlay, FileText, BookOpen, Eye, ExternalLink, X, Link2, CheckCircle, Edit, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CenterLMSPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('classes');
  const [liveClasses, setLiveClasses] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  // Schedule Class Dialog State
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [schedulingClass, setSchedulingClass] = useState(false);
  const [classForm, setClassForm] = useState({
    title: '',
    course: '',
    batch_ids: [],  // Changed to array for multiple selection
    instructor: '',
    date: '',
    time: '',
    duration: '60',
    meet_link: '',
    description: ''
  });
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);

  // Toggle batch selection
  const toggleBatchSelection = (batchId) => {
    setClassForm(prev => ({
      ...prev,
      batch_ids: prev.batch_ids.includes(batchId)
        ? prev.batch_ids.filter(id => id !== batchId)
        : [...prev.batch_ids, batchId]
    }));
  };

  // Get selected batch names for display
  const getSelectedBatchNames = () => {
    if (classForm.batch_ids.length === 0) return 'All Students';
    return batches
      .filter(b => classForm.batch_ids.includes(b.id))
      .map(b => b.batch_name || b.name)
      .join(', ');
  };

  // Edit Class Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editingClassBatchIds, setEditingClassBatchIds] = useState([]);
  const [showEditBatchDropdown, setShowEditBatchDropdown] = useState(false);

  // Toggle batch selection for edit
  const toggleEditBatchSelection = (batchId) => {
    setEditingClassBatchIds(prev => 
      prev.includes(batchId)
        ? prev.filter(id => id !== batchId)
        : [...prev, batchId]
    );
  };

  // Open Edit Dialog
  const handleEditClass = (cls) => {
    // Convert time from "10:00 AM" to "10:00" format for input
    let time24 = '';
    if (cls.time) {
      const match = cls.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (match) {
        let hours = parseInt(match[1]);
        const minutes = match[2];
        const ampm = match[3].toUpperCase();
        if (ampm === 'PM' && hours !== 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
        time24 = `${hours.toString().padStart(2, '0')}:${minutes}`;
      }
    }
    
    setEditingClass({
      ...cls,
      time24: time24
    });
    setEditingClassBatchIds(cls.batch_ids || []);
    setEditDialogOpen(true);
  };

  // Update Class Handler
  const handleUpdateClass = async (e) => {
    e.preventDefault();
    
    if (!editingClass.title || !editingClass.course || !editingClass.date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      // Format time to 12-hour format if changed
      let formattedTime = editingClass.time;
      if (editingClass.time24) {
        const [hours, minutes] = editingClass.time24.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        formattedTime = `${hour12}:${minutes} ${ampm}`;
      }
      
      const selectedBatchNames = editingClassBatchIds.length > 0
        ? batches.filter(b => editingClassBatchIds.includes(b.id)).map(b => b.batch_name || b.name)
        : ['All Students'];
      
      const payload = {
        title: editingClass.title,
        course: editingClass.course,
        batch_ids: editingClassBatchIds,
        batch_names: selectedBatchNames,
        instructor: editingClass.instructor || 'Instructor',
        date: editingClass.date,
        time: formattedTime,
        duration: parseInt(editingClass.duration) || 60,
        meet_link: editingClass.meet_link || '',
        description: editingClass.description || ''
      };
      
      await axios.put(`${API}/center/classes/${editingClass.id}`, payload, { headers: getAuthHeaders() });
      
      toast.success('Class updated successfully! / क्लास अपडेट हो गई!');
      setEditDialogOpen(false);
      setEditingClass(null);
      setEditingClassBatchIds([]);
      fetchData();
    } catch (error) {
      console.error('Update class error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update class');
    }
  };

  // Delete Class Handler
  const handleDeleteClass = async (classId) => {
    if (!confirm('Are you sure you want to delete this class?')) return;
    
    try {
      await axios.delete(`${API}/center/classes/${classId}`, { headers: getAuthHeaders() });
      toast.success('Class deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
    fetchCourses();
    fetchBatches();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      setCourses(res.data || []);
    } catch (error) {
      console.log('Could not fetch courses');
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await axios.get(`${API}/center/batches`, { headers: getAuthHeaders() });
      setBatches(res.data || []);
    } catch (error) {
      console.log('Could not fetch batches');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch scheduled classes from backend
      const res = await axios.get(`${API}/center/classes`, { headers: getAuthHeaders() });
      setLiveClasses(res.data || []);
      
      // Fetch recordings from backend
      try {
        const recRes = await axios.get(`${API}/center/recordings`, { headers: getAuthHeaders() });
        setRecordings(recRes.data || []);
      } catch {
        setRecordings([]);
      }
      
      // Fetch materials from backend
      try {
        const matRes = await axios.get(`${API}/center/materials`, { headers: getAuthHeaders() });
        setMaterials(matRes.data || []);
      } catch {
        setMaterials([]);
      }
    } catch (error) {
      console.error('Fetch LMS data error:', error);
      setLiveClasses([]);
    } finally {
      setLoading(false);
    }
  };

  // Schedule Class Handler
  const handleScheduleClass = async (e) => {
    e.preventDefault();
    
    if (!classForm.title || !classForm.course || !classForm.date || !classForm.time) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSchedulingClass(true);
    try {
      // Format time to 12-hour format
      const [hours, minutes] = classForm.time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const formattedTime = `${hour12}:${minutes} ${ampm}`;
      
      // Get batch names for display
      const selectedBatchNames = classForm.batch_ids.length > 0
        ? batches.filter(b => classForm.batch_ids.includes(b.id)).map(b => b.batch_name || b.name)
        : ['All Students'];
      
      const payload = {
        title: classForm.title,
        course: classForm.course,
        batch_ids: classForm.batch_ids,  // Multiple batches
        batch_names: selectedBatchNames,  // For display
        instructor: classForm.instructor || 'Instructor',
        date: classForm.date,
        time: formattedTime,
        duration: parseInt(classForm.duration) || 60,
        meet_link: classForm.meet_link || '',
        description: classForm.description || '',
        status: 'upcoming'
      };
      
      await axios.post(`${API}/center/classes`, payload, { headers: getAuthHeaders() });
      
      toast.success('Class scheduled successfully! / क्लास शेड्यूल हो गई!');
      setScheduleDialogOpen(false);
      setClassForm({
        title: '', course: '', batch_ids: [], instructor: '', 
        date: '', time: '', duration: '60', meet_link: '', description: ''
      });
      setShowBatchDropdown(false);
      fetchData();
    } catch (error) {
      console.error('Schedule class error:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule class');
    } finally {
      setSchedulingClass(false);
    }
  };

  const startClass = (classId) => {
    const cls = liveClasses.find(c => c.id === classId);
    if (cls?.meet_link) {
      window.open(cls.meet_link, '_blank');
    } else {
      toast.success('Starting live class...');
      window.open('https://meet.google.com', '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-lms-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Learning Management</h1>
          <p className="text-slate-500 mt-1">Manage live classes, recordings & materials</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button 
            onClick={() => setScheduleDialogOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2"
          >
            <Plus className="w-4 h-4" />
            Schedule Class
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5" />
            </div>
            <div>
              <p className="text-red-100 text-sm">Live Now</p>
              <p className="text-2xl font-bold">{liveClasses.filter(c => c.status === 'live').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Scheduled</p>
              <p className="text-2xl font-bold">{liveClasses.filter(c => c.status === 'upcoming').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <MonitorPlay className="w-5 h-5" />
            </div>
            <div>
              <p className="text-purple-100 text-sm">Recordings</p>
              <p className="text-2xl font-bold">{recordings.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-emerald-100 text-sm">Materials</p>
              <p className="text-2xl font-bold">{materials.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'classes', label: 'Live Classes', icon: Video },
          { id: 'recordings', label: 'Recordings', icon: MonitorPlay },
          { id: 'materials', label: 'Materials', icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === tab.id 
                ? 'border-purple-600 text-purple-600' 
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Live Classes Tab */}
      {activeTab === 'classes' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {liveClasses.map((cls) => (
            <motion.div
              key={cls.id}
              whileHover={{ y: -5 }}
              className={`bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-xl transition-all ${
                cls.status === 'live' ? 'border-red-200' : 'border-slate-200'
              }`}
            >
              <div className={`px-5 py-3 ${cls.status === 'live' ? 'bg-red-500' : 'bg-gradient-to-r from-purple-500 to-blue-500'} text-white`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{cls.course}</span>
                  {cls.status === 'live' && (
                    <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      LIVE
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-slate-900 text-lg mb-2">{cls.title}</h3>
                <p className="text-sm text-slate-500 mb-3">By {cls.instructor}</p>
                
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {cls.date}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {cls.time}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-slate-500">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{cls.students || 0} students</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      onClick={() => handleEditClass(cls)}
                      size="sm" 
                      variant="outline"
                      className="h-8 w-8 p-0 border-blue-200 hover:bg-blue-50"
                      title="Edit Class"
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button 
                      onClick={() => handleDeleteClass(cls.id)}
                      size="sm" 
                      variant="outline"
                      className="h-8 w-8 p-0 border-red-200 hover:bg-red-50"
                      title="Delete Class"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                    <Button 
                      onClick={() => startClass(cls.id)}
                      size="sm" 
                      className={cls.status === 'live' ? 'bg-red-500 hover:bg-red-600' : 'bg-purple-600 hover:bg-purple-700'}
                    >
                      {cls.status === 'live' ? (
                        <><Play className="w-4 h-4 mr-1" /> Join</>
                      ) : (
                        <><Video className="w-4 h-4 mr-1" /> Start</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Recordings Tab */}
      {activeTab === 'recordings' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recordings.map((rec) => (
            <motion.div
              key={rec.id}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all"
            >
              <div className="h-32 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                <MonitorPlay className="w-16 h-16 text-purple-400" />
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-slate-900">{rec.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{rec.course}</p>
                
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-3">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {rec.duration}
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {rec.views} views
                  </div>
                </div>
                
                <Button variant="outline" className="w-full mt-4 gap-2">
                  <Play className="w-4 h-4" />
                  Watch Recording
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Material</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Course</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Size</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {materials.map((mat) => (
                <tr key={mat.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        mat.type === 'PDF' ? 'bg-red-100' : 'bg-amber-100'
                      }`}>
                        <FileText className={`w-5 h-5 ${
                          mat.type === 'PDF' ? 'text-red-600' : 'text-amber-600'
                        }`} />
                      </div>
                      <span className="font-medium text-slate-900">{mat.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{mat.course}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      mat.type === 'PDF' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {mat.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{mat.size}</td>
                  <td className="px-4 py-3">
                    <Button variant="ghost" size="sm" className="gap-1">
                      <ExternalLink className="w-4 h-4" />
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Schedule Class Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Schedule Class / क्लास शेड्यूल करें
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleScheduleClass} className="space-y-4 mt-4">
            <div>
              <Label>Class Title / क्लास का नाम *</Label>
              <Input
                value={classForm.title}
                onChange={(e) => setClassForm({...classForm, title: e.target.value})}
                placeholder="e.g., AutoCAD Basics - Day 1"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Course / कोर्स *</Label>
                <select
                  value={classForm.course}
                  onChange={(e) => setClassForm({...classForm, course: e.target.value})}
                  className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                  required
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.name}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <Label>Batches (Optional) / बैच चुनें</Label>
                <div 
                  onClick={() => setShowBatchDropdown(!showBatchDropdown)}
                  className="w-full min-h-10 px-3 py-2 border rounded-lg bg-white text-sm cursor-pointer flex items-center justify-between"
                >
                  <span className={classForm.batch_ids.length === 0 ? 'text-slate-400' : 'text-slate-900'}>
                    {classForm.batch_ids.length === 0 
                      ? 'All Students' 
                      : `${classForm.batch_ids.length} batch(es) selected`}
                  </span>
                  <svg className={`w-4 h-4 transition-transform ${showBatchDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {/* Multi-select dropdown */}
                {showBatchDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {batches.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500 text-center">No batches available</div>
                    ) : (
                      <>
                        <div 
                          onClick={() => setClassForm({...classForm, batch_ids: []})}
                          className="flex items-center gap-2 p-3 hover:bg-slate-50 cursor-pointer border-b"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            classForm.batch_ids.length === 0 ? 'bg-purple-600 border-purple-600' : 'border-slate-300'
                          }`}>
                            {classForm.batch_ids.length === 0 && (
                              <CheckCircle className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className="text-sm">All Students (No specific batch)</span>
                        </div>
                        {batches.map((batch) => (
                          <div 
                            key={batch.id}
                            onClick={() => toggleBatchSelection(batch.id)}
                            className="flex items-center gap-2 p-3 hover:bg-slate-50 cursor-pointer"
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              classForm.batch_ids.includes(batch.id) ? 'bg-purple-600 border-purple-600' : 'border-slate-300'
                            }`}>
                              {classForm.batch_ids.includes(batch.id) && (
                                <CheckCircle className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">{batch.batch_name || batch.name}</span>
                              <span className="text-xs text-slate-500 ml-2">({batch.course_name || batch.course})</span>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
                
                {/* Selected batches display */}
                {classForm.batch_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {batches
                      .filter(b => classForm.batch_ids.includes(b.id))
                      .map(batch => (
                        <span 
                          key={batch.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
                        >
                          {batch.batch_name || batch.name}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-purple-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBatchSelection(batch.id);
                            }}
                          />
                        </span>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <Label>Instructor Name / इंस्ट्रक्टर</Label>
              <Input
                value={classForm.instructor}
                onChange={(e) => setClassForm({...classForm, instructor: e.target.value})}
                placeholder="e.g., Ramesh Sir"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date / तारीख *</Label>
                <Input
                  type="date"
                  value={classForm.date}
                  onChange={(e) => setClassForm({...classForm, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Time / समय *</Label>
                <Input
                  type="time"
                  value={classForm.time}
                  onChange={(e) => setClassForm({...classForm, time: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Duration (mins)</Label>
                <select
                  value={classForm.duration}
                  onChange={(e) => setClassForm({...classForm, duration: e.target.value})}
                  className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                >
                  <option value="30">30 mins</option>
                  <option value="45">45 mins</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Meeting Link (Google Meet / Zoom)
              </Label>
              <Input
                value={classForm.meet_link}
                onChange={(e) => setClassForm({...classForm, meet_link: e.target.value})}
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
              />
            </div>
            
            <div>
              <Label>Description / विवरण</Label>
              <Input
                value={classForm.description}
                onChange={(e) => setClassForm({...classForm, description: e.target.value})}
                placeholder="Topics to be covered..."
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setScheduleDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={schedulingClass}
                className="bg-gradient-to-r from-purple-600 to-blue-600 gap-2"
              >
                {schedulingClass ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4" />
                )}
                Schedule Class
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Class / क्लास एडिट करें
            </DialogTitle>
          </DialogHeader>
          
          {editingClass && (
            <form onSubmit={handleUpdateClass} className="space-y-4 mt-4">
              <div>
                <Label>Class Title / क्लास का नाम *</Label>
                <Input
                  value={editingClass.title || ''}
                  onChange={(e) => setEditingClass({...editingClass, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course / कोर्स *</Label>
                  <select
                    value={editingClass.course || ''}
                    onChange={(e) => setEditingClass({...editingClass, course: e.target.value})}
                    className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.name}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Label>Batches / बैच</Label>
                  <div 
                    onClick={() => setShowEditBatchDropdown(!showEditBatchDropdown)}
                    className="w-full min-h-10 px-3 py-2 border rounded-lg bg-white text-sm cursor-pointer flex items-center justify-between"
                  >
                    <span className={editingClassBatchIds.length === 0 ? 'text-slate-400' : 'text-slate-900'}>
                      {editingClassBatchIds.length === 0 
                        ? 'All Students' 
                        : `${editingClassBatchIds.length} batch(es)`}
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${showEditBatchDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  
                  {showEditBatchDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      <div 
                        onClick={() => setEditingClassBatchIds([])}
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer border-b text-sm"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          editingClassBatchIds.length === 0 ? 'bg-purple-600 border-purple-600' : 'border-slate-300'
                        }`}>
                          {editingClassBatchIds.length === 0 && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span>All Students</span>
                      </div>
                      {batches.map((batch) => (
                        <div 
                          key={batch.id}
                          onClick={() => toggleEditBatchSelection(batch.id)}
                          className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            editingClassBatchIds.includes(batch.id) ? 'bg-purple-600 border-purple-600' : 'border-slate-300'
                          }`}>
                            {editingClassBatchIds.includes(batch.id) && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <span>{batch.batch_name || batch.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label>Instructor / इंस्ट्रक्टर</Label>
                <Input
                  value={editingClass.instructor || ''}
                  onChange={(e) => setEditingClass({...editingClass, instructor: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date / तारीख *</Label>
                  <Input
                    type="date"
                    value={editingClass.date?.split('T')[0] || ''}
                    onChange={(e) => setEditingClass({...editingClass, date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Time / समय</Label>
                  <Input
                    type="time"
                    value={editingClass.time24 || ''}
                    onChange={(e) => setEditingClass({...editingClass, time24: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Duration</Label>
                  <select
                    value={editingClass.duration || '60'}
                    onChange={(e) => setEditingClass({...editingClass, duration: e.target.value})}
                    className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                  >
                    <option value="30">30 mins</option>
                    <option value="45">45 mins</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Meeting Link
                </Label>
                <Input
                  value={editingClass.meet_link || ''}
                  onChange={(e) => setEditingClass({...editingClass, meet_link: e.target.value})}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Update Class
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterLMSPage;
